import {
  AfterViewInit, Component, computed, ElementRef, HostBinding, inject, input, OnInit, output, viewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

import { SdLibConfig } from '../../sd-lib-config';
import { DeviceService } from '../../_services/device.service';
import type { IProductCustomer } from './product-customer.interface';
import { ILink } from '../../_interfaces/link.interface';
import { ISdFormComponentItem } from '../forms/form-component-interface.model';
import { IconSizeEnum } from '../../_enums/icon.enum';
import { ImageRatioEnum } from '../../_enums/image-ratio.enum';
import { SkeletonAppearanceEnum } from '../../_enums/skeleton.enum';
import { SdBannerComponent } from '../medias/banner/banner.component';
import { SdButtonIconComponent } from '../buttons/button-icon/button-icon.component';
import { SdFormSelectComponent } from '../forms/form-select/form-select.component';
import { SdIconComponent } from '../icons/icon/icon.component';
import { SdTitleComponent } from '../title/title.component';
import { SdLinkComponent } from '../links/link/link.component';
import { SdListComponent } from '../lists/list/list.component';
import { SdSkeletonComponent } from '../skeleton/skeleton.component';
import { SdConvertedPriceComponent } from '../converted-price/converted-price.component';

@Component({
  selector: 'sd-product-customer',
  templateUrl: './product-customer.component.html',
  standalone: true,
  imports: [
    SdTitleComponent,
    SdSkeletonComponent,
    SdBannerComponent,
    SdFormSelectComponent,
    NgClass,
    NgTemplateOutlet,
    SdLinkComponent,
    SdIconComponent,
    SdListComponent,
    SdButtonIconComponent,
    SlicePipe,
    TranslateModule,
    SdConvertedPriceComponent
  ]
})
export class SdProductCustomerComponent implements AfterViewInit, OnInit {
  indexFollow = input(true);
  isLoading = input(false);
  year = input([1980, new Date().getFullYear()]);
  productInfo = input<IProductCustomer>();
  isReadOnly = input(false);
  hasNoActions = input(false);
  quantityDisabled = input(false);
  canAddToCart = input(false);
  isRegistered = input(false);
  /**
   * Allow to display "documents" and guarantee buttons
   */
  displayProductAssets = input(true);
  isMinified = input(false);
  isWishList = input(false);
  customLinks = input(false);
  links = input<ILink[]>();
  isOrder = input(false);
  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  titleLevel = input(4);
  isBundle = input<boolean>();
  showAllRef = input(false);
  displayExtendedWarranty = input<boolean>();
  sourceCurrencyIsocode = input<string>(undefined);
  class = input('');

  favorite = output<string>();
  delete = output<string>();
  cart = output<string>();
  quantity = output<number>();
  warranty = output<void>();
  instruction = output<void>();
  openRecommendedPriceModal = output<void>();
  clicked = output<void>();

  quantitySelector = viewChild<SdFormSelectComponent<number>>('quantitySelector');
  daySelector = viewChild<SdFormSelectComponent<number>>('daySelector');
  monthSelector = viewChild<SdFormSelectComponent<number>>('monthSelector');
  yearSelector = viewChild<SdFormSelectComponent<number>>('yearSelector');
  readonly productCustomerMedia = viewChild<ElementRef>('productCustomerMedia');

  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly deviceService = inject(DeviceService);
  private readonly sdLibConfig = inject(SdLibConfig);
  private readonly router = inject(Router);

  isMobile = toSignal(this.deviceService.isMobile);

  updatedSelectedValue = computed(() => this.productInfo()?.quantity ?? 1);

  itemQty = computed(() => {
    return Array(this.productInfo()?.maxQty || 6)
      .fill('')
      .map((x, i) => {
        return {
          value: i + 1
        };
      });
  });

  yearArr = computed(() => {
    return Array(this.year()[1] - this.year()[0] + 1)
      .fill('')
      .map((x, i) => {
        return {
          value: this.year()[0] + i
        };
      });
  });

  skeletonAppearanceEnum = SkeletonAppearanceEnum;
  imageRatioEnum = ImageRatioEnum;
  imageRatio: ImageRatioEnum;
  imageHasBorder: boolean;
  showWishList: boolean;
  iconSize = IconSizeEnum;
  quantityForm: UntypedFormGroup;
  days = 31;
  daysArr: ISdFormComponentItem[] = [];
  months = 12;
  monthsArr: ISdFormComponentItem[] = [];
  daysInMonth;
  currentDay: ISdFormComponentItem = { value: new Date().getDate() };
  currentYear: ISdFormComponentItem = { value: new Date().getFullYear() };
  currentMonth: ISdFormComponentItem = { value: new Date().getMonth() + 1 };
  selectedDate: { day: number; month: number; year: number };
  defaultSelectedDay;
  defaultSelectedMonth;
  defaultSelectedYear;
  defaultMedia = {
    altText: 'Missing Image',
    code: 'missingImage',
    mime: 'image',
    url: '/assets/images/missing-image.png'
  };
  numberOfRefSavToDisplay = 2;

  @HostBinding('class')
  get hostClasses(): string {
    return this.computedHostClasses();
  }

  computedHostClasses = computed(() => {
    return [
      this.class(),
      this.isReadOnly() ? 'is-readonly' : '',
      this.productInfo()?.bundle ? 'has-bundle' : '',
      this.showAllRef() ? 'has-show-all-ref' : ''
    ]
      .filter(Boolean)
      .join(' ');
  });

  ngOnInit() {
    this.daysInMonth = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ).getDate();

    this.daysArr = Array(this.daysInMonth)
      .fill('')
      .map((x, i) => {
        return {
          value: i + 1
        };
      });

    this.monthsArr = Array(this.months)
      .fill('')
      .map((x, i) => {
        return {
          value: i + 1
        };
      });

    this.defaultSelectedDay = this.currentDay;
    this.defaultSelectedMonth = this.currentMonth;
    this.defaultSelectedYear = this.currentYear;

    const { productImageRatio, productImageHasBorder, wishList } = this.sdLibConfig.get();

    this.imageRatio = productImageRatio;
    this.imageHasBorder = productImageHasBorder;
    this.showWishList = wishList;
  }

  ngAfterViewInit() {
    this.quantityForm = this.formBuilder.group({
      quantitySelector: { value: 1 }
    });
  }

  addToCart() {
    this.cart.emit(this.productInfo().code);
  }

  addToFavorite() {
    this.favorite.emit(this.productInfo().code);
  }

  deleteProduct() {
    this.delete.emit(this.productInfo().code);
  }

  productWarranty() {
    this.warranty.emit();
  }

  productInstruction() {
    this.instruction.emit();
  }

  notifyQuantity() {
    if (this.quantitySelector()) {
      this.quantity.emit(this.quantitySelector().internalField.value);
    }
  }

  updateRegistrationDate(day: boolean) {
    let selectedDay = this.daySelector()?.internalField.value;
    const selectedMonth = this.monthSelector()?.internalField.value;
    const selectedYear = this.yearSelector()?.internalField.value;
    if (!day) {
      const getDayByMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      if (getDayByMonth) {
        this.daysArr = Array(getDayByMonth)
          .fill('')
          .map((x, i) => {
            return {
              value: i + 1
            };
          });
      }

      if (
        this.daysArr?.filter((dayNumber) => dayNumber.value === selectedDay)
          ?.length > 0
      ) {
        this.defaultSelectedDay = { value: selectedDay };
      } else {
        selectedDay = getDayByMonth;
        this.defaultSelectedDay = { value: selectedDay };
      }
    }

    this.selectedDate = {
      day: selectedDay,
      month: selectedMonth,
      year: selectedYear
    };
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  onLinkClick() {
    this.clicked.emit();
  }

  onClickReadMore(url?: string) {
    if (url) {
      this.router.navigate([url], { queryParams: { openRef: true } });
    }
  }
}
